/**
 * Vòng đời một phiên học — Prompt.md §22, §23, §26 (quyết định #14, #15).
 *
 * Không đọc giờ hệ thống, không giữ timer: mọi phương thức nhận `now` từ ngoài. Chỗ duy nhất đọc
 * đồng hồ thật trong toàn hệ thống là `MonitoringController.clock` (Bước 15) — nhờ vậy cả phiên
 * học có thể được replay từ debug log để tái hiện bug tuning.
 */

import { defaultSessionConfig, type SessionConfig } from "@studystraight/shared-config";
import type {
  MonitorSnapshot,
  PostureIssueType,
  SessionOptions,
  SessionStatus,
  StudySession,
} from "@studystraight/shared-types";

import { SessionAnalytics } from "./SessionAnalytics.js";
import type { SessionSnapshot } from "./SessionRepository.js";
import { StudyTimer, plannedDurationMs, type TimerEvent } from "./StudyTimer.js";

export interface SessionStartOptions extends SessionOptions {
  /**
   * Issue không đo được trong phiên này (§13 Option C) — lấy từ
   * `resolveExcludedIssues(baseline)`. Report dựa vào đây để không hiển thị "Nghiêng người:
   * 0 lần" khi thực ra là không đo được.
   */
  excludedIssues: readonly PostureIssueType[];
}

export interface SessionPushResult {
  /** ms đã học, đã trừ pause. */
  elapsedMs: number;
  remainingMs: number | null;
  timerEvents: readonly TimerEvent[];
  /** `true` khi đã tới lúc ghi snapshot xuống storage (quyết định #15). */
  snapshotDue: boolean;
}

const IDLE_RESULT: SessionPushResult = {
  elapsedMs: 0,
  remainingMs: null,
  timerEvents: [],
  snapshotDue: false,
};

export interface SessionManagerOptions {
  config?: SessionConfig;
  /**
   * Sinh id phiên. Mặc định dựa trên `startTime` — hai phiên không thể bắt đầu trong cùng một
   * milli giây, và tránh `Math.random()` để phiên replay được từ debug log.
   */
  createId?: (startTime: number) => string;
}

export class SessionManager {
  private readonly config: SessionConfig;
  private status: SessionStatus = "IDLE";
  private options: SessionStartOptions | null = null;

  private id = "";
  private startTime = 0;
  private pausedDuration = 0;
  private pausedAt: number | null = null;
  private lastElapsed = 0;
  private lastSnapshotAt = 0;
  private alerts = 0;

  private analytics = new SessionAnalytics(defaultSessionConfig);
  private timer: StudyTimer | null = null;

  constructor(private readonly managerOptions: SessionManagerOptions = {}) {
    this.config = managerOptions.config ?? defaultSessionConfig;
    this.analytics = new SessionAnalytics(this.config);
  }

  get state(): SessionStatus {
    return this.status;
  }

  get isRunning(): boolean {
    return this.status === "RUNNING";
  }

  get sessionId(): string {
    return this.id;
  }

  get elapsedMs(): number {
    return this.lastElapsed;
  }

  start(options: SessionStartOptions, now: number): void {
    this.options = options;
    this.id = (this.managerOptions.createId ?? ((t) => `session-${t}`))(now);
    this.startTime = now;
    this.pausedDuration = 0;
    this.pausedAt = null;
    this.lastElapsed = 0;
    this.lastSnapshotAt = now;
    this.alerts = 0;
    this.analytics = new SessionAnalytics(this.config);
    const planned = plannedDurationMs(options);
    this.timer = new StudyTimer({
      ...(planned === undefined ? {} : { plannedDurationMs: planned }),
      eyeBreakEnabled: options.eyeBreakEnabled,
      config: this.config,
    });
    this.status = "RUNNING";
  }

  /**
   * Nạp một snapshot từ `PostureMonitor`.
   *
   * Frame mất tracking **không** được tính vào thống kê: trẻ rời khỏi bàn 10 phút mà vẫn cộng
   * điểm sẽ tạo ra một Report nói rằng con ngồi rất đẹp suốt 10 phút đó. Cũng vì thế mà thời gian
   * học (`duration`) và số frame đo được là hai đại lượng khác nhau, và Report chỉ dùng %.
   */
  push(snapshot: MonitorSnapshot): SessionPushResult {
    if (this.status !== "RUNNING" || this.timer === null) return IDLE_RESULT;

    const elapsed = this.elapsedAt(snapshot.timestamp);
    this.lastElapsed = elapsed;

    if (snapshot.tracking !== "LOST" && snapshot.result.excludedMetrics.length < 4) {
      this.analytics.pushScore(snapshot.result.postureScore, elapsed);
    }
    this.analytics.pushEvents(snapshot.events);

    const tick = this.timer.tick(elapsed);
    const snapshotDue = snapshot.timestamp - this.lastSnapshotAt >= this.config.snapshotIntervalMs;
    if (snapshotDue) this.lastSnapshotAt = snapshot.timestamp;

    return {
      elapsedMs: elapsed,
      remainingMs: tick.remainingMs,
      timerEvents: tick.events,
      snapshotDue,
    };
  }

  /** Gọi khi AlertEngine thực sự phát ra một cảnh báo — không phải mỗi `PostureEvent` (§21). */
  recordAlert(): void {
    this.alerts += 1;
  }

  pause(now: number): void {
    if (this.status !== "RUNNING") return;
    this.status = "PAUSED";
    this.pausedAt = now;
  }

  resume(now: number): void {
    if (this.status !== "PAUSED") return;
    // Quyết định #14: thời gian pause không tính vào `duration`. Trẻ đi uống nước 5 phút không
    // phải 5 phút học, và cũng không phải 5 phút "tư thế không xác định".
    if (this.pausedAt !== null) this.pausedDuration += now - this.pausedAt;
    this.pausedAt = null;
    this.status = "RUNNING";
    this.lastSnapshotAt = now;
  }

  end(now: number): StudySession {
    if (this.status === "PAUSED" && this.pausedAt !== null) {
      this.pausedDuration += now - this.pausedAt;
      this.pausedAt = null;
    }
    const session = this.buildSession(now, true);
    this.status = "ENDED";
    return session;
  }

  /** Phiên dở dang để ghi xuống storage (quyết định #15). */
  toSnapshot(now: number): SessionSnapshot {
    return { session: this.buildSession(now, false), finalized: false, savedAt: now };
  }

  private elapsedAt(now: number): number {
    const paused = this.pausedAt === null ? 0 : Math.max(0, now - this.pausedAt);
    return Math.max(0, now - this.startTime - this.pausedDuration - paused);
  }

  private buildSession(now: number, ended: boolean): StudySession {
    const options = this.options;
    if (options === null) throw new Error("SessionManager.start() chưa được gọi");

    const summary = this.analytics.summarize();
    const duration = this.elapsedAt(now);
    const planned = plannedDurationMs(options);

    return {
      id: this.id,
      startTime: this.startTime,
      ...(ended ? { endTime: now } : {}),
      duration,
      pausedDuration: this.pausedDuration,
      averagePostureScore: summary.averagePostureScore,
      goodPosturePercentage: summary.goodPosturePercentage,
      alertCount: this.alerts,
      issues: summary.issues,
      childId: options.childId,
      profileId: options.profileId,
      placement: options.placement,
      excludedIssues: [...options.excludedIssues],
      scoreSamples: summary.scoreSamples,
      ...(planned === undefined ? {} : { plannedDurationMs: planned }),
      eyeBreakCount: this.timer?.eyeBreakCount ?? 0,
      schemaVersion: 1,
    };
  }
}
